import { useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { IconPlus } from "@tabler/icons-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import BottomPanel from "../app/panels/BottomPanel";
import { displayToast } from "../app/AppToast";
import { apiClient, useApiRevalidate } from "../../services/api";
import { ClientError } from "../../../../api/src/shared/request";
import { reloadCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { gaPushEvent } from "../../tools/analytics/base.analytics";

/**
 * Full-width "Create a playlist" button that opens a bottom sheet to create a
 * new (empty) playlist — mirrors the create step of the add-to-playlist flow
 * (PlaylistPanel), but standalone since that panel is coupled to adding content.
 */
export default function CreatePlaylistButton() {
  const { t } = useTranslation();
  const revalidate = useApiRevalidate();
  const [isOpen, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [isSaving, setSaving] = useState(false);

  async function handleCreate() {
    if (name.length === 0) return;
    try {
      setSaving(true);
      await apiClient.post("/webapp/private/playlists", {
        json: { title: name },
      });
      gaPushEvent("playlist_created", { origin: "profile", title: name });
      await revalidate("/webapp/private/playlists/me");
      displayToast(t("Collection created. Added to your profile."), "success");
      await reloadCurrentUser();
      setName("");
      setOpen(false);
    } catch (error) {
      if (error instanceof ClientError && error.responseStatus === 409) {
        displayToast(
          t(
            "🥺 This playlist title already exists. Give it a unique sweet name."
          ),
          "error"
        );
      } else {
        displayToast(t("Something went wrong. Please try again."), "error");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="lg"
        className="w-full"
        leftIcon={<IconPlus />}
        onClick={() => {
          setOpen(true);
        }}
      >
        {t("Create a playlist")}
      </Button>
      <BottomPanel
        isOpen={isOpen}
        onClose={() => {
          setOpen(false);
        }}
        title={t("Create new playlist")}
        displayCloseButton
        actions={
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={name.length === 0 || isSaving}
            onClick={handleCreate}
          >
            {t("Save")}
          </Button>
        }
      >
        <div className="overflow-hidden px-5 pt-[30px] pb-[150px]">
          <Input
            value={name}
            onInput={(e: ChangeEvent<HTMLInputElement>) => {
              setName(e.target.value);
            }}
            placeholder={t("Playlist name")}
          />
        </div>
      </BottomPanel>
    </>
  );
}
